// Route, Vehicle, and Driver filter configurations

import {
	createDateRangeFilter,
	createEntityLookupFilter,
	createKeywordFilter,
	createStatusFilter,
} from './factories';
import type { FilterRegistry } from './types';

export const routeFilters: FilterRegistry = {
	// Despite the legacy `route*` filter ids and the `objectTable: 'route'`
	// SQL alias, every entry below is consumed only by the *Trips* list
	// (`TRIP_LIST_CONFIG.filters.activeFilters`). Post-Phase-A the
	// per-day instance table IS `route` (trip data) and the recurring
	// blueprint list registers no filters. So the user-facing labels here
	// are trip-flavored. The filter ids stay as `route*` because changing
	// them would invalidate every saved filter the user has stored on
	// the Trips list — a no-op-for-the-user rename that breaks state.
	routeSearch: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search trips...',
			objectTable: 'route',
			listKeys: ['route'],
		}),
		queryParamName: 'routeSearch',
	},

	routeDateRange: {
		...createDateRangeFilter({
			label: 'Trip Date',
			objectTable: 'route',
			dateField: 'date',
			listKeys: ['route'],
		}),
		queryParamName: 'routeDateRange',
	},

	routeDriver: {
		...createEntityLookupFilter({
			label: 'Driver',
			placeholder: 'Select driver...',
			apiEndpoint: '/users',
			objectTable: 'route',
			fieldName: 'driverId',
			listKeys: ['route'],
			displayFields: {
				id: 'id',
				primary: 'fullName',
			},
		}),
		queryParamName: 'routeDriver',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	routeVehicle: {
		...createEntityLookupFilter({
			label: 'Vehicle',
			placeholder: 'Select vehicle...',
			apiEndpoint: '/vehicles',
			objectTable: 'route',
			fieldName: 'vehicleId',
			listKeys: ['route'],
			displayFields: {
				id: 'id',
				primary: 'name',
			},
		}),
		queryParamName: 'routeVehicle',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	routeStatus: {
		...createStatusFilter({
			label: 'Status',
			objectTable: 'route',
			fieldName: 'status',
			listKeys: ['route'],
			options: [
				// `pending_load` was added 2026-05-12 — sits between
				// dispatch-commit and pre-trip. Warehouse manager owns it.
				{ id: 'pending_load', label: 'Pending Load' },
				{ id: 'scheduled', label: 'Scheduled' },
				{ id: 'in-progress', label: 'In Progress' },
				{ id: 'completed', label: 'Completed' },
				{ id: 'failed', label: 'Failed' },
				{ id: 'cancelled', label: 'Cancelled' },
			],
		}),
		queryParamName: 'routeStatus',
	},

	// Vehicle filters
	vehicleSearch: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search vehicles...',
			objectTable: 'vehicle',
			listKeys: ['vehicle'],
		}),
		queryParamName: 'vehicleSearch',
	},

	vehicleStatus: {
		...createStatusFilter({
			label: 'Status',
			objectTable: 'vehicle',
			fieldName: 'status',
			listKeys: ['vehicle'],
			options: [
				{ id: 'active', label: 'Active' },
				{ id: 'inactive', label: 'Inactive' },
				{ id: 'maintenance', label: 'Maintenance' },
				{ id: 'offline', label: 'Offline' },
			],
		}),
		queryParamName: 'vehicleStatus',
	},

	vehicleType: {
		...createKeywordFilter({
			label: 'Type',
			placeholder: 'Filter by type...',
			objectTable: 'vehicle',
			listKeys: ['vehicle'],
		}),
		queryParamName: 'vehicleType',
	},

	// Driver filters
	driverSearch: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search drivers...',
			objectTable: 'driver',
			listKeys: ['driver'],
		}),
		queryParamName: 'driverSearch',
	},

	driverStatus: {
		...createStatusFilter({
			label: 'Status',
			objectTable: 'driver',
			fieldName: 'status',
			listKeys: ['driver'],
			options: [
				{ id: 'active', label: 'Active' },
				{ id: 'inactive', label: 'Inactive' },
				{ id: 'on-route', label: 'On Route' },
			],
		}),
		queryParamName: 'driverStatus',
	},
};
